import { crossEnvClient, CrossEnvClient } from '../../../../api/crossEnvClient';
import { ApplyItemResult, ApplyReport, ApplySections, SyncPlan } from './types';

const userGroupKey = (email: string, groupName: string): string => `${email}::${groupName}`;

function errorResult(err: unknown): ApplyItemResult {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return {
    ok: false,
    error_code: e.response?.data?.error,
    error_message: e.response?.data?.message ?? e.message ?? 'Request failed.',
  };
}

const emptyReport = (): ApplyReport => ({
  roles: { create: {}, update: {} },
  users: { invite: {}, reassign: {} },
  groups: { create: {}, update: {} },
  userGroups: { assign: {}, unassign: {} },
});

async function applyRoles(client: CrossEnvClient, plan: SyncPlan, report: ApplyReport): Promise<void> {
  for (const r of plan.roles.create) {
    if (r.blocked) {
      report.roles.create[r.role_code] = {
        ok: false,
        error_code: 'catalog_gap',
        error_message: `Blocked — target is missing permission code(s): ${r.missingPermissions.join(', ')}.`,
      };
      continue;
    }
    try {
      await client.post('/api/admin/rbac-v2/roles', {
        role_code: r.role_code,
        role_name: r.role_name,
        description: r.description,
        permissions: r.permissions,
      });
      report.roles.create[r.role_code] = { ok: true };
    } catch (err) {
      report.roles.create[r.role_code] = errorResult(err);
    }
  }

  for (const r of plan.roles.update) {
    if (r.blocked) {
      report.roles.update[r.role_code] = {
        ok: false,
        error_code: 'catalog_gap',
        error_message: `Blocked — target is missing permission code(s): ${r.missingPermissions.join(', ')}.`,
      };
      continue;
    }
    try {
      await client.put(`/api/admin/rbac-v2/roles/${encodeURIComponent(r.role_code)}`, {
        role_name: r.role_name,
        description: r.description,
        permissions: r.permissions,
      });
      report.roles.update[r.role_code] = { ok: true };
    } catch (err) {
      report.roles.update[r.role_code] = errorResult(err);
    }
  }
}

/** Invites missing users (POST /api/admin/users, then PUT their v2 role),
 * then reassigns matched users whose v2 role differs. Returns the
 * email→target_user_id map for newly-invited users, so the groups step can
 * resolve `target_user_id: null` entries in the user-group assign plan. */
async function applyUsers(
  client: CrossEnvClient,
  plan: SyncPlan,
  report: ApplyReport,
): Promise<Map<string, number>> {
  const invitedIdByEmail = new Map<string, number>();

  for (const u of plan.users.invite) {
    try {
      // The legacy `config.app_user_role` FK requires a role_code that
      // exists in `config.app_role` — 'default' is bootstrapped on every
      // env. The user's actual v2 role is set in the follow-up PUT.
      const { data } = await client.post<{ id: number }>('/api/admin/users', {
        email: u.email,
        display_name: u.display_name,
        role_code: 'default',
      });
      invitedIdByEmail.set(u.email, data.id);
      try {
        await client.put(`/api/admin/rbac-v2/users/${data.id}/role`, { role_code: u.role_v2_code });
        report.users.invite[u.email] = { ok: true };
      } catch (roleErr) {
        report.users.invite[u.email] = {
          ...errorResult(roleErr),
          error_message: `User created but setting its role failed: ${errorResult(roleErr).error_message}`,
        };
      }
    } catch (err) {
      report.users.invite[u.email] = errorResult(err);
    }
  }

  for (const u of plan.users.reassign) {
    try {
      await client.put(`/api/admin/rbac-v2/users/${u.target_user_id}/role`, { role_code: u.to_role_code });
      report.users.reassign[u.email] = { ok: true };
    } catch (err) {
      report.users.reassign[u.email] = errorResult(err);
    }
  }

  return invitedIdByEmail;
}

/** Creates/updates tenant groups, then syncs user↔group assignments.
 * Groups first (assignments may reference a just-created group); user-group
 * assign/unassign resolves any pending invite/group ids captured earlier in
 * THIS apply run. */
async function applyGroups(
  client: CrossEnvClient,
  plan: SyncPlan,
  report: ApplyReport,
  invitedIdByEmail: Map<string, number>,
): Promise<void> {
  const createdGroupIdByName = new Map<string, number>();

  for (const g of plan.groups.create) {
    try {
      const { data } = await client.post<{ id: number }>('/api/admin/rbac-v2/tenant-groups', {
        name: g.name,
        level: g.level,
        members: g.members,
      });
      createdGroupIdByName.set(g.name.trim().toLowerCase(), data.id);
      report.groups.create[g.name] = { ok: true };
    } catch (err) {
      report.groups.create[g.name] = errorResult(err);
    }
  }

  for (const g of plan.groups.update) {
    try {
      await client.put(`/api/admin/rbac-v2/tenant-groups/${g.target_id}`, {
        name: g.name,
        level: g.level,
        members: g.members,
      });
      report.groups.update[g.name] = { ok: true };
    } catch (err) {
      report.groups.update[g.name] = errorResult(err);
    }
  }

  const resolveUserId = (email: string, planned: number | null): number | undefined =>
    planned ?? invitedIdByEmail.get(email);
  const resolveGroupId = (groupName: string, planned: number | null): number | undefined =>
    planned ?? createdGroupIdByName.get(groupName.trim().toLowerCase());

  for (const a of plan.userGroups.assign) {
    const key = userGroupKey(a.email, a.group_name);
    const userId = resolveUserId(a.email, a.target_user_id);
    const groupId = resolveGroupId(a.group_name, a.target_group_id);
    if (userId === undefined || groupId === undefined) {
      report.userGroups.assign[key] = {
        ok: false,
        error_code: 'unresolved_dependency',
        error_message: 'The user or group this assignment depends on was not created — apply that section first.',
      };
      continue;
    }
    try {
      await client.post(`/api/admin/rbac-v2/users/${userId}/groups`, { group_id: groupId });
      report.userGroups.assign[key] = { ok: true };
    } catch (err) {
      report.userGroups.assign[key] = errorResult(err);
    }
  }

  for (const u of plan.userGroups.unassign) {
    const key = userGroupKey(u.email, u.group_name);
    try {
      await client.delete(`/api/admin/rbac-v2/users/${u.target_user_id}/groups/${u.target_group_id}`);
      report.userGroups.unassign[key] = { ok: true };
    } catch (err) {
      report.userGroups.unassign[key] = errorResult(err);
    }
  }
}

/** Executes `plan` against `env`, sequentially per entity so one item's
 * failure never aborts the rest — dependency order: roles → invites → role
 * reassignment → groups → user-group assign/unassign. `sections` gates
 * which of the 4 product-spec sections actually fire (selective apply);
 * `sections.groups` covers BOTH group CRUD and user-group assignment, since
 * the spec treats those as one section. */
export async function applySyncPlan(env: string, plan: SyncPlan, sections: ApplySections): Promise<ApplyReport> {
  const client = crossEnvClient(env);
  const report = emptyReport();

  if (sections.roles) {
    await applyRoles(client, plan, report);
  }

  let invitedIdByEmail = new Map<string, number>();
  if (sections.users) {
    invitedIdByEmail = await applyUsers(client, plan, report);
  }

  if (sections.groups) {
    await applyGroups(client, plan, report, invitedIdByEmail);
  }

  return report;
}
